import React, { useMemo } from "react";
import {
  Area,
  AreaChart,
  Bar,
  Brush,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const formatMetricValue = (value, dataKey) => {
  if (dataKey === "volume") {
    return Number(value || 0).toLocaleString("en-IN");
  }
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const TrendTooltipCard = ({ active, payload, label, title, metricLabelMap }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        minWidth: "190px",
        borderRadius: "12px",
        border: "1px solid rgba(34, 197, 94, 0.22)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(240,253,244,0.98) 100%)",
        boxShadow: "0 16px 32px rgba(15,23,42,0.16)",
        padding: "10px 12px",
        backdropFilter: "blur(8px)",
        transition: "all 180ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        <span
          style={{
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "#16A34A",
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: "11px", color: "#4B5563", fontWeight: 600 }}>
          {label || "--"}
        </span>
      </div>

      {payload.map((entry) => {
        const key = String(entry?.dataKey || "");
        return (
          <div
            key={key}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              marginTop: "6px",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "11px",
                color: "#475569",
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "999px",
                  background: entry?.color || "#10B981",
                  boxShadow: `0 0 0 2px ${(entry?.color || "#10B981")}22`,
                }}
              />
              {metricLabelMap[key] || key}
            </span>
            <span style={{ fontSize: "12px", color: "#0F172A", fontWeight: 700 }}>
              {formatMetricValue(entry?.value, key)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const RANGE_POINTS = {
  "1D": 120,
  "1W": 150,
  "1M": 220,
};

const buildLabels = (range, points) => {
  if (range === "1D") {
    return Array.from({ length: points }, (_, i) => {
      const minuteStep = 3;
      const totalMinutes = 9 * 60 + 15 + i * minuteStep;
      const hour = Math.floor(totalMinutes / 60) % 24;
      const minute = totalMinutes % 60;
      return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    });
  }
  if (range === "1W") {
    return Array.from({ length: points }, (_, i) => {
      const day = 20 + Math.floor(i / 16);
      return i % 16 === 0 ? `${day}` : "";
    });
  }
  return Array.from({ length: points }, (_, i) => {
    const day = 1 + Math.floor(i / 7);
    return i % 7 === 0 ? `${day}` : "";
  });
};

const seededNoise = (seed, index) => {
  const value = Math.sin(seed * 9.13 + index * 23.71) * 43758.5453;
  return value - Math.floor(value);
};

const movingAverage = (series, period) =>
  series.map((_, index) => {
    if (index < period - 1) return series[index];
    const slice = series.slice(index - period + 1, index + 1);
    return slice.reduce((sum, value) => sum + value, 0) / slice.length;
  });

const buildSeries = ({ basePrice, seed, trendBias, range }) => {
  const points = RANGE_POINTS[range] || RANGE_POINTS["1D"];
  const labels = buildLabels(range, points);

  let prev = Math.max(1, basePrice * 0.93);
  const rows = labels.map((label, idx) => {
    const noise = seededNoise(seed, idx) - 0.5;
    const wave = Math.sin((idx / Math.max(points - 1, 1)) * Math.PI * 3.8) * 0.0038;
    const drift = trendBias * 0.00055 + noise * 0.0045 + wave;
    const price = Math.max(1, prev * (1 + drift));
    const volume = Math.max(
      120,
      Math.round(
        120 + Math.abs(price - prev) * 460 + seededNoise(seed + 2.1, idx) * 140,
      ),
    );
    prev = price;
    return {
      idx,
      label,
      price: Number(price.toFixed(2)),
      volume,
    };
  });

  const prices = rows.map((row) => row.price);
  const maFast = movingAverage(prices, 9);
  const maSlow = movingAverage(prices, 24);

  return rows.map((row, index) => ({
    ...row,
    maFast: Number(maFast[index].toFixed(2)),
    maSlow: Number(maSlow[index].toFixed(2)),
    delta:
      index === 0
        ? 0
        : Number((row.price - rows[index - 1].price).toFixed(2)),
    volumeColor:
      index === 0
        ? "#86EFAC"
        : row.price >= rows[index - 1].price
          ? "#10B981"
          : "#EF4444",
  }));
};

const MultiPaneStockChart = ({
  basePrice = 1000,
  seed = 1,
  trendBias = 1,
  range = "1D",
}) => {
  const data = useMemo(
    () => buildSeries({ basePrice, seed, trendBias, range }),
    [basePrice, seed, trendBias, range],
  );

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "grid",
        gridTemplateRows: "4fr 1.25fr",
        gap: "10px",
      }}
    >
      <div
        style={{
          minHeight: 0,
          borderRadius: "10px",
          background: "#F3F4F6",
          border: "1px solid #D9DEE7",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#D1D5DB" strokeDasharray="0" vertical horizontal />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#4B5563" }}
              axisLine={false}
              tickLine={false}
              minTickGap={18}
            />
            <YAxis
              orientation="right"
              tick={{ fontSize: 10, fill: "#3F3F46" }}
              axisLine={false}
              tickLine={false}
              domain={["dataMin - 50", "dataMax + 50"]}
              tickFormatter={(val) => `$${Number(val).toFixed(2)}`}
              width={78}
            />
            <Tooltip
              cursor={{ stroke: "#16A34A", strokeOpacity: 0.32, strokeDasharray: "4 4" }}
              isAnimationActive
              animationDuration={260}
              animationEasing="ease-out"
              wrapperStyle={{ transition: "opacity 180ms ease, transform 180ms ease" }}
              content={
                <TrendTooltipCard
                  title="Profit Trend"
                  metricLabelMap={{
                    price: "Price",
                    maFast: "MA Fast",
                    maSlow: "MA Slow",
                    volume: "Volume",
                  }}
                />
              }
            />
            <Bar dataKey="volume" fill="#86EFAC" opacity={0.78} barSize={2}>
              {data.map((entry, idx) => (
                <Cell key={`vol-${idx}`} fill={entry.volumeColor} />
              ))}
            </Bar>
            <Line type="monotone" dataKey="price" stroke="#10B981" strokeWidth={1.6} dot={false} />
            <Line type="monotone" dataKey="maFast" stroke="#22C55E" strokeWidth={1.7} dot={false} />
            <Line type="monotone" dataKey="maSlow" stroke="#EF4444" strokeWidth={1.9} dot={false} />
            <Brush
              dataKey="label"
              height={22}
              stroke="#94A3B8"
              travellerWidth={10}
              fill="#E5E7EB"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          minHeight: 0,
          borderRadius: "8px",
          border: "1px solid #9CA3AF",
          background: "#ECEFF1",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="miniFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22C55E" stopOpacity={0.34} />
                <stop offset="100%" stopColor="#22C55E" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" hide />
            <YAxis hide domain={["dataMin - 50", "dataMax + 50"]} />
            <Tooltip
              cursor={{ stroke: "#16A34A", strokeOpacity: 0.3, strokeDasharray: "4 4" }}
              isAnimationActive
              animationDuration={240}
              animationEasing="ease-out"
              wrapperStyle={{ transition: "opacity 180ms ease, transform 180ms ease" }}
              content={<TrendTooltipCard title="Range View" metricLabelMap={{ price: "Range" }} />}
            />
            <Area type="monotone" dataKey="price" stroke="#22C55E" strokeWidth={2} fill="url(#miniFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MultiPaneStockChart;
